import { Fragment, type ReactNode } from 'react'
import type { CSSProperties } from 'react'
import { ligneEnNoeuds, type RenduOptions } from './inline'

/**
 * Le rendu d'un texte du corpus — énoncé, option, note de correction.
 *
 * Le dialecte est **fermé** (`Modules\Corpus\Domain\RichText`) : ce qui n'y
 * figure pas est refusé à l'écriture, donc ne peut pas arriver ici. Ce
 * composant en est le pendant à l'affichage, et il s'arrête aux mêmes bornes —
 * pas de titres, pas de liens, pas de HTML libre.
 *
 * **Un tableau va dans un conteneur défilant** (RG-D8) : sur un écran de
 * téléphone, un tableau de cinq colonnes déborde et pousse le fil hors de
 * l'écran. Le faire défiler LUI plutôt que la page est la seule façon de le
 * garder lisible sans le rétrécir jusqu'à l'illisible.
 */
export function RichText({
  texte,
  options = {},
  className,
  avecTitres = false,
}: {
  texte: string
  options?: RenduOptions | undefined
  className?: string | undefined
  /**
   * Rend les titres `###` et `####` — RÉSERVÉ au contenu de cours.
   *
   * Le dialecte des ITEMS reste fermé : un énoncé de question n'a pas de
   * sous-titres, et en accepter ouvrirait la porte à des cartes qui se
   * mettent en page toutes seules. Une leçon, elle, est un texte long
   * structuré — sans titres, ses `###` s'affichaient tels quels, en clair,
   * au milieu du cours.
   */
  avecTitres?: boolean | undefined
}) {
  return <div className={className}>{blocs(texte, options, avecTitres)}</div>
}

type Bloc =
  | { genre: 'titre'; niveau: 2 | 3 | 4; texte: string }
  | { genre: 'paragraphe'; lignes: string[] }
  | { genre: 'liste'; ordonnee: boolean; elements: string[] }
  | { genre: 'citation'; lignes: string[] }
  | { genre: 'tableau'; lignes: string[] }

function blocs(texte: string, options: RenduOptions, avecTitres: boolean): ReactNode[] {
  return decouper(texte, avecTitres).map((bloc, rang) => rendre(bloc, `b${String(rang)}`, options))
}

/**
 * Regroupe les lignes en blocs.
 *
 * Volontairement linéaire : le dialecte n'a ni imbrication ni continuation, ce
 * qui rend l'analyse triviale — et c'est précisément pour cela qu'il est fermé.
 */
function decouper(texte: string, avecTitres = false): Bloc[] {
  const blocs: Bloc[] = []
  let courant: Bloc | null = null

  const fermer = (): void => {
    if (courant !== null) blocs.push(courant)
    courant = null
  }

  for (const ligne of texte.split(/\r?\n/)) {
    const nue = ligne.trim()

    if (nue === '') {
      fermer()
      continue
    }

    // Les titres se ferment tout seuls : ils tiennent sur une ligne, et un
    // titre collé au paragraphe suivant serait lu comme sa première phrase.
    // RG-3.14 précisée — **du niveau 2 au niveau 4**. Le corpus rédigé emploie
    // `##` pour ses sections ; n'accepter que `###` laissait « ## Introduction »
    // s'afficher dièses compris, en plein milieu de la leçon.
    const titre = avecTitres ? /^(#{2,4})\s+(.*)$/.exec(nue) : null

    if (titre !== null) {
      const diese = titre[1]?.length ?? 2

      fermer()
      blocs.push({
        genre: 'titre',
        niveau: diese === 4 ? 4 : diese === 3 ? 3 : 2,
        texte: titre[2] ?? '',
      })
      continue
    }

    const puce = /^[-*]\s+(.*)$/.exec(nue)
    const numero = /^\d+[.)]\s+(.*)$/.exec(nue)
    const citation = /^>\s?(.*)$/.exec(nue)
    const tableau = nue.startsWith('|') && nue.endsWith('|')

    if (puce !== null || numero !== null) {
      const ordonnee = numero !== null
      const contenu = (numero ?? puce)?.[1] ?? ''

      if (courant?.genre !== 'liste' || courant.ordonnee !== ordonnee) {
        fermer()
        courant = { genre: 'liste', ordonnee, elements: [] }
      }

      courant.elements.push(contenu)
      continue
    }

    if (citation !== null) {
      if (courant?.genre !== 'citation') {
        fermer()
        courant = { genre: 'citation', lignes: [] }
      }

      courant.lignes.push(citation[1] ?? '')
      continue
    }

    if (tableau) {
      if (courant?.genre !== 'tableau') {
        fermer()
        courant = { genre: 'tableau', lignes: [] }
      }

      courant.lignes.push(nue)
      continue
    }

    /*
     * **La suite d'une puce enroulée reste dans la puce** — [DECISIONS §229].
     *
     * Le corpus est replié à soixante-seize colonnes : une puce un peu longue
     * occupe deux lignes, et la seconde ne porte pas de tiret. Elle tombait
     * donc ici, **fermait la liste** et ouvrait un paragraphe — la puce
     * s'affichait tronquée, et sa fin en dessous, hors de la liste.
     *
     * Markdown appelle cela une continuation paresseuse : une ligne non vide
     * qui suit un élément de liste sans marqueur lui appartient. Une ligne
     * vide ferme le bloc bien avant d'arriver ici, donc un vrai paragraphe qui
     * suit une liste n'est jamais avalé.
     */
    if (courant?.genre === 'liste' && courant.elements.length > 0) {
      const dernier = courant.elements.length - 1
      courant.elements[dernier] = `${courant.elements[dernier] ?? ''} ${nue}`
      continue
    }

    if (courant?.genre !== 'paragraphe') {
      fermer()
      courant = { genre: 'paragraphe', lignes: [] }
    }

    courant.lignes.push(nue)
  }

  fermer()

  return blocs
}

function rendre(bloc: Bloc, cle: string, options: RenduOptions): ReactNode {
  if (bloc.genre === 'titre') {
    // On DESCEND d'un cran : `##` rend un `h3`, `###` un `h4`, `####` un `h5`.
    // Dans une leçon, `h1` est le titre de l'écran et `h2` celui de la
    // section ; les doubler casserait la navigation par titres d'un lecteur
    // d'écran, qui s'en sert pour annoncer où l'on se trouve.
    const Balise = ({ 2: 'h3', 3: 'h4', 4: 'h5' } as const)[bloc.niveau]

    // ── L'ÉCHELLE DES TITRES — RG-C21, §393 ────────────────────────────────
    //
    // **Elle était plate aux deux bouts.** `##` faisait 20 px extrabold, soit
    // exactement le titre de la leçon ; `####` faisait 15 px, soit exactement
    // le corps. Entre les deux, 20 → 17 → 15 donne des rapports de 1,18 et
    // 1,13 : trop serrés pour que l'œil y lise des niveaux.
    //
    // **Le quatrième niveau change de FORME, pas seulement de taille.** À force
    // de descendre on passe sous le corps de texte, où un titre maigre
    // disparaît. En petites majuscules interlettrées il devient une étiquette :
    // lisible comme un titre, sans concurrencer le texte.
    // L'échelle complète d'une leçon, du haut vers le bas :
    //
    //     24 px  le titre de la leçon        (`Lecon.tsx`, h2)
    //     19 px  la grande partie, EN ACCENT (`Lecon.tsx`, h3)
    //     18 px  `##`                        (ici, h3)
    //     16 px  `###`                       (ici, h4)
    //     13 px  `####`, en étiquette        (ici, h5)
    //     15 px  le corps
    //
    // **Le corps est à 15 px** : un titre en dessous de 16 ne se lit plus comme
    // un titre, d'où le plancher à 16 pour `###`.
    const style = {
      2: 'mb-4 mt-12 text-[18px] font-extrabold',
      3: 'mb-3 mt-10 text-[16px] font-bold',
      4: 'mb-2 mt-8 text-[13px] font-bold uppercase tracking-[0.08em]',
    }[bloc.niveau]

    // **La couleur d'accent appartient à la grande partie**, pas ici : deux
    // niveaux colorés se concurrenceraient au lieu de se hiérarchiser. Le
    // dernier niveau, lui, s'efface en `--texte-2` — c'est une étiquette.
    const couleur = {
      2: 'var(--texte-1)',
      3: 'var(--texte-1)',
      4: 'var(--texte-2)',
    }[bloc.niveau]

    return (
      <Balise key={cle} className={style} style={{ color: couleur }}>
        {ligneEnNoeuds(bloc.texte, options)}
      </Balise>
    )
  }

  if (bloc.genre === 'liste') {
    const Balise = bloc.ordonnee ? 'ol' : 'ul'

    return (
      <Balise
        key={cle}
        // Même respiration que les paragraphes — RG-C21. Une liste collée au
        // texte qui l'annonce se lit comme sa suite, pas comme une énumération.
        className={`my-6 ml-5 flex flex-col gap-3 ${bloc.ordonnee ? 'list-decimal' : 'list-disc'}`}
      >
        {bloc.elements.map((element, rang) => (
          <li key={`${cle}-${String(rang)}`}>{ligneEnNoeuds(element, options)}</li>
        ))}
      </Balise>
    )
  }

  if (bloc.genre === 'citation') {
    const marque = marqueurDe(bloc.lignes[0] ?? '')
    const lignes = marque.estUnMarqueur ? bloc.lignes.slice(1) : bloc.lignes

    return (
      <blockquote
        key={cle}
        className="my-5 rounded-r-lg border-l-4 py-3 pl-4 pr-3"
        style={styleRubrique(marque.estUnMarqueur ? marque.famille : deduite(lignes[0] ?? ''))}
      >
        {inline(lignes, cle, options)}
      </blockquote>
    )
  }

  if (bloc.genre === 'tableau') {
    return <Tableau key={cle} lignes={bloc.lignes} options={options} />
  }

  return (
    // **L'espace entre paragraphes vaut l'interligne** — RG-C21, §393. Il
    // valait 16 px pour un interligne de 24 : une coupure de paragraphe se
    // lisait donc MOINS qu'une ligne, et le texte se présentait en bloc.
    <p key={cle} className="my-6 first:mt-0 last:mb-0">
      {inline(bloc.lignes, cle, options)}
    </p>
  )
}

/**
 * Les lignes d'un même paragraphe — **recollées avant d'être analysées**.
 *
 * [DECISIONS §229]. Elles étaient analysées UNE PAR UNE, puis jointes par une
 * espace. Le rendu était juste tant qu'une emphase tenait sur une ligne ; dès
 * qu'elle enjambait un retour souple, aucune des deux moitiés ne trouvait son
 * motif — et le dialecte **rendait ses propres marqueurs au candidat**.
 *
 * Le corpus est replié à soixante-seize colonnes de bout en bout : une phrase
 * de plus de dix mots en gras enjambe presque toujours. Ben l'a vu dans le
 * cours de littérature, étoiles comprises, dans une leçon publiée.
 *
 * **Un retour souple est une espace** — c'est la définition même de Markdown.
 * On recolle donc avant de découper, et rien d'autre ne change : le paragraphe
 * rendait déjà une seule suite de nœuds, séparés par cette même espace.
 */
/**
 * Une ligne qui OUVRE un item d'énumération — « a) », « B. », « 3) ».
 *
 * Le corpus est plié à soixante-seize colonnes : une ligne qui commence ainsi
 * n'est jamais la suite de la précédente. Le motif est volontairement étroit —
 * une seule lettre ou un seul nombre, puis `)` ou `.`, puis une espace — pour
 * qu'une phrase de prose ne s'y reconnaisse pas par accident.
 */
const OUVRE_UN_ITEM = /^(?:[a-zA-Z]|[0-9]{1,2})[).]\s/

/**
 * La couleur d'une boîte de cours, selon ce que sa première ligne annonce.
 *
 * Ben, le 4 septembre 2026 : « une coloration de l'arrière-plan des boîtes
 * dépendamment de la rubrique ».
 *
 * **La rubrique se LIT dans le corpus, elle ne s'invente pas.** Les citations
 * du cours ouvrent sur un intitulé en gras — « Exemple résolu » revient
 * soixante-huit fois, « Exemple concret » quatorze, « À retenir » dix, et l'on
 * trouve aussi « Point à vérifier » ou « La faute à ne pas commettre ». Il n'y
 * a **pas de vocabulaire fermé** : on reconnaît donc une FAMILLE au premier
 * mot, et l'on retombe sur un fond neutre quand rien ne correspond. Inventer
 * une taxonomie fermée aurait laissé la moitié des boîtes hors du système.
 *
 * **Les couleurs viennent des jetons SÉMANTIQUES déjà là**, jamais d'une
 * palette nouvelle : `--accent` pour ce qui s'établit, `--juste` pour ce qui
 * s'illustre, `--faux` pour ce qui se rate, `--attente` pour ce qui se retient.
 * Elles suivent ainsi les quatre thèmes du produit — `light`, `dim`,
 * `lights-out` et le réglage système — sans qu'on ait à les redéfinir une seule
 * fois. Une palette écrite à la main aurait été fausse dans trois thèmes sur
 * quatre.
 *
 * Le fond est un `color-mix` à 10 % sur la surface : assez pour distinguer les
 * familles d'un coup d'œil, assez peu pour que le texte garde son contraste.
 */
/*
 * **La rubrique se DÉCLARE, elle ne se devine pas** — docs/38.
 *
 * Mesure du 3 septembre 2026 : sur 1 854 intitules de citation, 1 371 (74 %)
 * ne portent aucun mot de rubrique — « Marmelade n'est pas dans le Nord. »
 * Déduire l'intention d'une phrase française par mots-clés est un pari, et une
 * boîte mal colorée enseigne une fausse catégorie au candidat.
 *
 * Les jetons sont ceux du thème, jamais une couleur littérale, et --accent est
 * exclu : il est choisi par l'utilisateur, donc impropre à porter un sens fixe.
 */
const MARQUEURS: Record<string, string> = {
  definition: 'notion',
  methode: 'demarche',
  exemple: 'juste',
  piege: 'faux',
  retenir: 'attente',
}

/* Compatibilité : le fonds non annoté garde sa couleur quand le mot y est. */
const MOTS: [string[], string][] = [
  [['contre-exemple', 'piege', 'attention', 'erreur', 'faute', 'confusion', 'eviter', 'ecueil'], 'faux'],
  [['exemple', 'application', 'illustration'], 'juste'],
  [['methode', 'demarche', 'procede', 'marche a suivre'], 'demarche'],
  [['definition', 'definir', 'notion', 'vocabulaire', 'rappel'], 'notion'],
  [['retenir', 'essentiel', 'savoir'], 'attente'],
]

/* Sans diacritiques ni casse : « Piège » et « piege » sont le même mot. */
function aplati(texte: string): string {
  return texte
    .normalize('NFD')
    .split('')
    .filter((c) => {
      const point = c.codePointAt(0) ?? 0
      return point < 0x0300 || point > 0x036f
    })
    .join('')
    .toLowerCase()
}

/* La première ligne vaut marqueur quand elle ne porte que [!nom]. */
function marqueurDe(ligne: string): { famille: string | undefined; estUnMarqueur: boolean } {
  const t = ligne.trim()
  if (!t.startsWith('[!') || !t.endsWith(']')) return { famille: undefined, estUnMarqueur: false }
  return { famille: MARQUEURS[aplati(t.slice(2, -1).trim())], estUnMarqueur: true }
}

function intituleDe(ligne: string): string {
  const t = ligne.trimStart()
  if (!t.startsWith('**')) return ''
  const fin = t.indexOf('**', 2)
  return fin < 0 ? '' : t.slice(2, fin).trim()
}

function deduite(premiereLigne: string): string | undefined {
  const intitule = aplati(intituleDe(premiereLigne))
  if (intitule === '') return undefined
  return MOTS.find(([mots]) => mots.some((mot) => intitule.includes(mot)))?.[1]
}

function styleRubrique(famille: string | undefined): CSSProperties {
  if (famille === undefined) {
    return {
      borderColor: 'var(--bordure)',
      background: 'var(--surface-2)',
      color: 'var(--texte-1)',
    }
  }

  return {
    borderColor: `var(--${famille}-fort)`,
    background: `color-mix(in oklab, var(--${famille}) 10%, var(--surface-2))`,
    color: 'var(--texte-1)',
  }
}

function inline(lignes: string[], cle: string, options: RenduOptions): ReactNode[] {
  /*
   * **Les lignes de prose se recollent, les items gardent leur ligne.**
   *
   * §229 recollait TOUT : « un retour souple est une espace », la définition
   * même de Markdown, posée parce qu'une emphase à cheval sur un repli perdait
   * son motif et rendait ses astérisques au candidat, dans un cours publié.
   * Cette raison-là tient toujours, et le recollage de la prose la sert.
   *
   * **Mais un item d'énumération n'est pas de la prose qui se replie.** Ben l'a
   * vu le 4 septembre 2026 : un exercice de français affichait ses cinq items
   * a) à e) en une seule phrase interminable. Les recoller détruit la seule
   * chose qui les rendait lisibles.
   *
   * On coupe donc en GROUPES : chaque item ouvre un groupe, et les lignes qui
   * le suivent s'y recollent — un item long qui se replie reste un item, et son
   * emphase à cheval trouve toujours son motif.
   */
  const groupes: string[][] = []

  for (const ligne of lignes) {
    if (groupes.length === 0 || OUVRE_UN_ITEM.test(ligne)) {
      groupes.push([ligne])
    } else {
      groupes[groupes.length - 1]?.push(ligne)
    }
  }

  return groupes.map((groupe, rang) => (
    <Fragment key={`${cle}-${String(rang)}`}>
      {rang > 0 && <br />}
      {ligneEnNoeuds(groupe.join(' '), options)}
    </Fragment>
  ))
}

function Tableau({ lignes, options }: { lignes: string[]; options: RenduOptions }) {
  const rangs = lignes
    // La ligne de séparation `|---|---|` porte l'alignement, pas des données.
    .filter((ligne) => !/^\|[\s:|-]+\|$/.test(ligne))
    .map((ligne) =>
      ligne
        .slice(1, -1)
        .split('|')
        .map((cellule) => cellule.trim()),
    )

  const [entete, ...corps] = rangs

  if (entete === undefined) return null

  return (
    // RG-D8 — le tableau défile, pas la page.
    <div className="my-5 -mx-4 overflow-x-auto px-4">
      <table className="w-full min-w-max border-collapse text-left">
        <thead>
          <tr className="border-b" style={{ borderColor: 'var(--bordure)' }}>
            {entete.map((cellule, rang) => (
              <th key={`h${String(rang)}`} className="px-2 py-1 font-bold">
                {ligneEnNoeuds(cellule, options)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {corps.map((cellules, ligne) => (
            <tr
              key={`r${String(ligne)}`}
              className="border-b last:border-0"
              style={{ borderColor: 'var(--bordure)' }}
            >
              {cellules.map((cellule, colonne) => (
                <td key={`c${String(colonne)}`} className="px-2 py-1">
                  {ligneEnNoeuds(cellule, options)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
